import { Component, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { NoteEntryService } from "ng/src/app/notes/shared/note-entry.service";
import { DatePipe } from "@angular/common";
import { of, Subject } from "rxjs";
import { retry, take, takeUntil } from "rxjs/operators";
import { AutoSyncType, CalendarControl, CalendarEventAction, LogUserSelection } from "ng/src/app/notes/shared/note-entry.model";
import { TemplateTypeId } from "ng/src/app/templates/shared/template.model";
import { OverlayPanel } from "primeng/overlaypanel";
import { LogBookRootScopeService } from "app/shared/model/app.model";
import { TagValue } from "ng/src/app/tags/tag.model";
import { Calendar } from "primeng/calendar";
import { UntypedFormControl } from "@angular/forms";
import { ConfirmationService } from "primeng/api";
import { AppStateService } from "ng/src/app/core/services/app-state.service";

@Component({
	selector: 'lb-note-entry-controls',
	templateUrl: './note-entry-controls.component.html',
	styleUrls: ['./note-entry-controls.component.scss'],
	providers: [DatePipe]
})
export class NoteEntryControlsComponent implements OnInit, OnDestroy {
	@ViewChild('overlayPanel') overlayPanel!: OverlayPanel;
	
	coCreatorCount = 0;
	coCreatorString = 'No Co-Creators';
	
	readonly helpRows = [
		'noteTime',
		'carryForward',
		'turnover',
		'attachments',
		'tags',
		'coCreators'
	];
	
	maxDate = new Date();
	yearRange = "";
	tagString = 'No Tags';
	
	dialogVisible = false;
	dialogMode: 'tags' | 'attachments' | 'coCreators' | 'help' = 'coCreators';
	
	noteTimeControl?: CalendarControl;
	carryForwardControl?: CalendarControl;
	
	inFlyDate?: Date;
	
	get displayDate() {
		return this.inFlyDate ?? this.properties?.noteTime ?? null;
	}
	
	get viewIsNarrow() {
		return this.entryService.viewIsNarrow;
	}
	
	get showCoCreators() {
		return !this.isOperational && !this.isPublishedNote;
	}
	
	private destroy$ = new Subject();
	
	constructor(
		private entryService: NoteEntryService,
		private datePipe: DatePipe,
		private confirmService: ConfirmationService,
		@Inject('$rootScope') private $rootScope: LogBookRootScopeService
	) {}
	
	ngOnInit(): void {
		this.updateMaxDate();
		this.entryService.coCreators$.pipe(
			takeUntil(this.destroy$)
		)
			.subscribe(() => this.updateCoCreatorCount());
		this.entryService.noteProperties$.pipe(
			takeUntil(this.destroy$)
		)
			.subscribe((properties) => {
				this.updateTagString(this.properties?.tagValues ?? []);
				if (properties) {
					this.noteTimeControl?.control?.setValue(properties.noteTime);
					this.carryForwardControl?.control?.setValue(properties.carryforward.endDate);
				}
			})
	}
	
	ngOnDestroy(): void {
		this.destroy$.next();
	}
	
	get mobileView() {
		return !AppStateService.hoverSupported;
	}
	
	get tagValues() {
		return this.properties?.tagValues ?? [];
	}
	set tagValues(val) {
		if (this.properties)
			this.properties.tagValues = val;
		this.updateTagString(val);
	}
	
	get isOperational() {
		return this.entryService.selectedTemplateType === TemplateTypeId.OPERATIONAL;
	}
	
	get coCreators() {
		return this.entryService.coCreators;
	}

	get attachmentCount() {
		return this.properties?.attachments.filter(fa => fa.description != 'Rich Text Image').length ?? 0;
	}
	get attachmentCountDisplay() {
		const count = this.attachmentCount;
		
		return (count > 0 ? count : '').toString();
	}
	
	get tagCount() {
		return this.properties?.tagValues?.length ?? 0;
	}
	get tagCountDisplay() {
		const count = this.tagCount;
		return (count > 0 ? count : '').toString();
	}
	
	get turnOverEnabled() {
		return !!this.properties?.turnover;
	}
	
	get carryForwardEnabled() {
		return !!this.properties?.carryforward?.enabled;
	}
	
	get properties() {
		return this.entryService.noteProperties;
	}
	
	get selectedLogId() {
		return this.entryService.selectedLog!.id;
	}
	
	get showEndTime() {
		return !!this.entryService.noteProperties?.carryforward.enabled;
	}

	get isPublishedNote() {
		return this.entryService.editingPublishedNote;
	}

	updateMaxDate() {
		this.maxDate = new Date();
		this.yearRange = `${this.maxDate.getFullYear() - 25}:${this.maxDate.getFullYear()}`
	}

	triggerNoteSync() {
		if (!this.entryService.selectedGroup && !this.entryService.selectedTemplate?.isDefaultTemplate) return;
		
		this.entryService.triggerSync(AutoSyncType.AUTO);
	}
	
	getHeaderText(mode = this.dialogMode) {
		switch (mode) {
			case "tags":
				return `${this.tagCount} Tags`;
			case 'attachments':
				return `${this.attachmentCount} Attachments`;
			case "coCreators":
				return `${this.coCreatorCount} Co-Creators`
			case "help":
				return "Note Properties"
		}
	}
	
	setDialogMode(mode: 'tags' | 'attachments' | 'coCreators' | 'help') {
		this.dialogMode = mode;
		this.dialogVisible = true;
	}
	
	changeUserSelected(logUserSelection: LogUserSelection) {
		logUserSelection.selected = !logUserSelection.selected;
		this.updateCoCreatorCount();
	}
	
	updateCoCreatorCount() {
		const coCreators = this.coCreators.filter(lgUsr => lgUsr.selected);
		this.coCreatorCount = coCreators.length;
		this.coCreatorString = coCreators.map(c => `${c.user.firstName} ${c.user.lastName}`).join('\n');
	}
	
	getEndTimeString(addDate = true): string {
		const date: Date | null = this.entryService.noteProperties?.carryforward?.endDate ?? null;
		
		if (!this.carryForwardEnabled)
			return 'Carry Forward Disabled';
		
		if (!(date instanceof Date) || isNaN(date.getDate()))
			return `Carry Forward${addDate ? ' Indefinitely' : ''}`;
		
		const now = new Date();
		const transformed = this.datePipe.transform(date, 'MM/dd/YYYY HH:mm');
		return `${now < date ? 'Carry forward until ' : 'Was carried forward until '} ${addDate ? transformed : ''}`;
	}
	
	openHelp() {
		this.$rootScope.$emit('openHelp', 'note-properties');
	}

	updateTagString(tagValues: TagValue[]) {
		this.tagString = tagValues.length ? tagValues.map(tv => tv.viewString).join('\n') : 'No Tags';
	}
	
	getCalendarControl(type: 'time' | 'carryForward', calendar: Calendar) {
		const key = type === 'time' ? 'noteTimeControl' : 'carryForwardControl';
		let control = this[key];
		if (control)
			return control.updateCalendar(calendar);
		
		const initValue = (type === 'time' ? this.properties?.noteTime : this.properties?.carryforward?.endDate) ?? null;
		
		control = new CalendarControl(calendar, new UntypedFormControl(initValue), this.destroy$, false, !(type === 'time' && this.isPublishedNote))
			.setOnCloseHandler(delegate => {
				const subject = new Subject<CalendarEventAction>();
				const diffMs = delegate.control.value instanceof Date ? this.differenceInMs(delegate.control.value) : 0;
				const diffTooGreat = diffMs > (12 * 60 * 60 * 1000) && type === 'time' && !this.isPublishedNote;
				const inFuture = !this.isPublishedNote && delegate.control.value instanceof Date && new Date() < delegate.control.value;
				
				if (!delegate.isInvalid)
					return of(CalendarEventAction.LEAVE);
				else {
					this.confirmService.confirm({
						key: 'invalidNoteTimeDialog',
						header: type === 'time' ? 'Invalid Note Time' : 'Invalid End Time',
						rejectButtonStyleClass: 'p-button-secondary',
						defaultFocus: 'accept',
						acceptLabel: 'Yes',
						rejectLabel: 'No',
						message: (diffTooGreat && type === 'time'
							? 'The provided date has a greater than 12 hours difference from now and cannot be used. Do you want to keep editing?'
							: inFuture && type === 'time'
								? 'The provided date cannot be in the future. Do you want to keep editing?'
								: 'The provided date is invalid. Do you want to keep editing?'),
						accept: () => subject.next(CalendarEventAction.FOCUS),
						reject: () => subject.next(CalendarEventAction.REVERT)
					})
				}
				return subject.pipe(take(1));
			})
			.setOnFinalizeHandler(value => {
				if (type === 'time')
					delete this.inFlyDate;
				
				if (!this.properties) return;
				else if  (type === 'time') {
					if (this.properties.noteTime == value) return;
					this.properties.noteTime = value;
				}
				else {
					if (this.properties.carryforward.endDate == value) return;
					this.properties.carryforward.endDate = value;
				}
				this.triggerNoteSync();
			})
			.setInputTextConverter({
				pattern: /^\d{4}$/g,
				onMatch: (input) => {
					if (this.isPublishedNote)
						return 'continue';
					
					const date = new Date();
					date.setHours(
						limitNumber(parseInt(`${input[0]}${input[1]}`), 0, 23),
						limitNumber(parseInt(`${input[2]}${input[3]}`), 0, 59),
						0,
						0
					);
					return isNaN(date.getTime()) ? null : date;
					
					function limitNumber(value: number, min: number, max: number) {
						if (isNaN(value) || value < min) return min;
						else if (value > max) return max;
						return value;
					}
				}
			})
			.setTransformer(value => {
				if (value instanceof Date) {
					value.setSeconds(0, 0);
					
					if (!this.isPublishedNote && type === 'time') {
						const now = new Date();
						
						if (value > now) {
							
							const retroValue = new Date( value.getTime() - (24 * 60 * 60 * 1000) );
							const diffMs = Math.abs(now.getTime() - retroValue.getTime());
							
							// Check if the difference is less than 12 hours
							if (diffMs <= (12 * 60 * 60 * 1000)) {
								// Set the date backwards by 24 hours to allow it to be posted as a retroactive note.
								value.setTime( retroValue.getTime() );
							}
						}
						else if (value.getHours() <= now.getHours() || (value.getHours() === now.getMinutes() && value.getMinutes() <= now.getMinutes()) && value.getDate() != now.getDate()) {
							// Correct the date if the hours and minutes are below the current time's and if the day is not the same
							value.setDate(now.getDate());
						}
					}
				}
				
				if (type === 'time')
					this.inFlyDate = value ?? undefined;
				
				return value;
			})
			.setValueValidator(value => {
				return type !== 'time' || !value || this.isPublishedNote || (this.differenceInMs(value) <= (12 * 60 * 60 * 1000) && new Date() > value);
			});
		
		return this[key] = control;
		
	}
	
	private differenceInMs(date: Date) {
		return Math.abs(new Date().getTime() - date.getTime());
	}
}